import React, { useContext, useCallback } from 'react';
import { Bell, BellFill } from 'react-bootstrap-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AppStateContext } from '../../contexts/AppStateContext';
import { StreamsContext } from '../../contexts/StreamsContext';
import { parseQuoteLast } from '../../utils/quoteNetChangePct';
import { useTheme } from '../../contexts/ThemeContext';
import { headerIconButtonStyle, headerIconButtonBg } from '../../utils/headerIconButtonStyle';

/**
 * AddAlertButton - Button to create a draft alert on the chart
 * When clicked, creates a draggable dashed line at the current price
 * User can position it, then it auto-submits when dragging completes
 */
const AddAlertButton = ({ ticker, isMobile = false, variant = 'default', compact = false }) => {
  const { colors, isLight } = useTheme();
  const { draftAlert, setDraftAlert, setShowAlertsOnChart } = useContext(AppStateContext);
  const { quotesBySymbol } = useContext(StreamsContext);

  // Check if there's an active draft alert for this ticker
  const hasDraftForTicker = draftAlert && draftAlert.ticker === ticker;
  const isHeaderVariant = variant === 'header';
  // headerCircle: desktop trade header — white ring/icon on dark, theme text on light header
  const isHeaderCircleVariant = variant === 'headerCircle';
  const isHeaderIconVariant = variant === 'headerIcon'; // Light-gray button + purple bell (trade header)

  const handleClick = useCallback((e) => {
    e.stopPropagation();

    // If there's already a draft alert, clear it (toggle off)
    if (hasDraftForTicker) {
      setDraftAlert(null);
      return;
    }

    // Get current price for this ticker
    const currentQuote = quotesBySymbol?.[ticker];
    const price = parseQuoteLast(currentQuote);

    if (!ticker || !Number.isFinite(price) || price <= 0) {
      return;
    }

    // Draft lines are part of alert price levels; turn alerts on so the draggable line is visible
    setShowAlertsOnChart(true);

    const draftAlertData = {
      ticker,
      price,
      alert_type: 'above', // Default to 'above', will determine based on drag direction
    };

    setDraftAlert(draftAlertData);
  }, [ticker, quotesBySymbol, hasDraftForTicker, setDraftAlert, setShowAlertsOnChart]);

  const circleFg = isLight ? colors.text : '#ffffff';
  const headerIconIdleBg = headerIconButtonBg(colors, { isLight });

  const baseStyle = isHeaderIconVariant
    ? headerIconButtonStyle(colors, { isLight, active: hasDraftForTicker })
    : isHeaderCircleVariant
    ? {
        backgroundColor: hasDraftForTicker ? (isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.12)') : 'transparent',
        border: `1px solid ${circleFg}`,
        color: circleFg,
        padding: 0,
        width: 32,
        height: 32,
        minWidth: 32,
        borderRadius: '50%',
        boxSizing: 'border-box',
      }
    : isHeaderVariant
    ? {
        backgroundColor: hasDraftForTicker ? colors.primary : colors.bgSecondary,
        border: hasDraftForTicker ? `1px solid ${colors.primary}` : `1px solid ${colors.border}`,
        color: hasDraftForTicker ? '#fff' : colors.text,
        padding: '6px 12px',
        borderRadius: 6,
        fontSize: compact ? 13 : 14,
        fontWeight: 500,
        minHeight: compact ? 32 : undefined,
      }
    : {
        backgroundColor: hasDraftForTicker ? colors.primary : (isLight ? colors.bgCard : 'rgba(0, 0, 0, 0.6)'),
        backdropFilter: hasDraftForTicker ? 'none' : (isLight ? 'none' : 'blur(4px)'),
        border: hasDraftForTicker ? `1px solid ${colors.primary}` : `1px solid ${colors.border}`,
        color: hasDraftForTicker ? '#fff' : colors.text,
        padding: isMobile ? '6px' : '6px',
        borderRadius: '4px',
        width: isMobile ? '32px' : '32px',
        height: isMobile ? '32px' : '32px',
        minWidth: isMobile ? '32px' : '32px',
      };

  const button = (
    <button
      type="button"
      onClick={handleClick}
      style={{
        ...baseStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isHeaderVariant ? (compact ? 5 : 6) : 0,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        outline: 'none',
        boxSizing: 'border-box',
      }}
      onMouseEnter={(e) => {
        if (!hasDraftForTicker) {
          if (isHeaderIconVariant) {
            e.currentTarget.style.opacity = '0.85';
          } else if (isHeaderCircleVariant) {
            e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)';
          } else if (isHeaderVariant) {
            e.currentTarget.style.backgroundColor = colors.bgSecondary;
            e.currentTarget.style.filter = 'brightness(1.1)';
          } else {
            e.currentTarget.style.backgroundColor = isLight ? colors.bgSecondary : 'rgba(0, 0, 0, 0.8)';
          }
        }
      }}
      onMouseLeave={(e) => {
        if (!hasDraftForTicker) {
          if (isHeaderIconVariant) {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.backgroundColor = headerIconIdleBg;
          } else if (isHeaderCircleVariant) {
            e.currentTarget.style.backgroundColor = 'transparent';
          } else if (isHeaderVariant) {
            e.currentTarget.style.backgroundColor = colors.bgSecondary;
            e.currentTarget.style.filter = 'none';
          } else {
            e.currentTarget.style.backgroundColor = isLight ? colors.bgCard : 'rgba(0, 0, 0, 0.6)';
          }
        }
      }}
      aria-label={hasDraftForTicker ? 'Cancel alert creation' : 'Create price alert'}
    >
      {hasDraftForTicker ? (
        <BellFill size={isHeaderCircleVariant ? 17 : isHeaderIconVariant ? 16 : compact && isHeaderVariant ? 15 : 16} />
      ) : (
        <Bell size={isHeaderCircleVariant ? 17 : isHeaderIconVariant ? 16 : compact && isHeaderVariant ? 15 : 16} color={isHeaderIconVariant ? colors.primary : undefined} />
      )}
    </button>
  );

  // Touch trade chrome: skip tooltip when caller marks mobile (headerIcon is also used on desktop)
  if (isMobile) {
    return button;
  }

  // On desktop, wrap with tooltip
  return (
    <OverlayTrigger
      placement="bottom"
      overlay={
        <Tooltip>
          {hasDraftForTicker 
            ? 'Cancel alert creation - Click to remove draft alert' 
            : 'Create alert - Click to add draggable alert line at current price'}
        </Tooltip>
      }
    >
      {button}
    </OverlayTrigger>
  );
};

export default AddAlertButton;
